import React, { useEffect, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { api } from "../../lib/api";
import { fmtNum } from "../../lib/format";
import { Loader2, TrendingUp, TrendingDown, Download, ArrowRightLeft, FileDown } from "lucide-react";
import { Button } from "../ui/button";
import ResellerStatementPdf from "../ResellerStatementPdf";
import ResellerOpenItemsPdf from "../ResellerOpenItemsPdf";
import { toast } from "sonner";

/**
 * Phase 101fk (Feb 2026) — Widget Balance revendeur.
 *
 * Affiche le solde à date + résumé (facturé / payé / avoirs) + accès rapide
 * au relevé de compte JSON. Le PDF viendra dans une deuxième couche.
 *
 * Alain : « Widget Solde à date sur la fiche revendeur et sur /factures ».
 */

function fmtDateInput(d) {
  return d.toISOString().slice(0, 10);
}

export default function ResellerBalanceWidget({ resellerId }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [balance, setBalance] = useState(null);
  const [statement, setStatement] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(fmtDateInput(firstOfMonth));
  const [to, setTo] = useState(fmtDateInput(today));
  const [showStatement, setShowStatement] = useState(false);

  useEffect(() => {
    if (!resellerId) return;
    setLoading(true);
    Promise.all([
      api.get(`/resellers/${resellerId}/balance`),
      api.get("/settings").catch(() => ({ data: {} })),
    ])
      .then(([b, s]) => { setBalance(b.data); setSettings(s.data || {}); })
      .catch(() => setBalance(null))
      .finally(() => setLoading(false));
  }, [resellerId]);

  const loadStatement = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/resellers/${resellerId}/statement`, {
        params: { from_date: from, to_date: to },
      });
      setStatement(data);
      setShowStatement(true);
    } catch (e) {
      setStatement(null);
    } finally {
      setLoading(false);
    }
  };

  // Phase 101fm (Feb 2026) — Alain : « Ajoute un bouton exporter le relevé de
  // compte revendeur en PDF corporate imprimable ». On génère le PDF via
  // React-PDF côté client (cohérent avec Invoice/DeliveryNote/CateringQuote).
  const downloadPdf = async () => {
    setLoading(true);
    try {
      // Charge le relevé si pas déjà fait, ou avec la période active si l'utilisateur a modifié from/to
      const { data } = await api.get(`/resellers/${resellerId}/statement`, {
        params: { from_date: from, to_date: to },
      });
      const blob = await pdf(<ResellerStatementPdf statement={data} settings={settings} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const cleanName = (data.reseller?.name || "reseller").replace(/[^a-zA-Z0-9]+/g, "-");
      link.download = `Releve-${cleanName}-${from}-${to}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Relevé PDF téléchargé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur génération PDF");
    } finally {
      setLoading(false);
    }
  };

  if (!balance && !loading) return null;

  const downloadOpenItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/resellers/${resellerId}/open-items`);
      const blob = await pdf(<ResellerOpenItemsPdf data={data} settings={settings} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Factures-ouvertes-${(data.reseller?.name || "revendeur").replace(/[^a-zA-Z0-9]+/g, "-")}-${data.as_of}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Relevé des factures ouvertes téléchargé : ${data.invoices.length} facture(s), net ${fmtNum(data.totals.net_due)} CHF`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur génération PDF");
    } finally {
      setLoading(false);
    }
  };

  const isDebtor = balance && balance.balance > 0.01;
  const isCreditor = balance && balance.balance < -0.01;

  return (
    <section className="bg-white border border-[#E5E5E0] rounded-lg p-5" data-testid="reseller-balance-widget">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C6B2F]">Compte client</p>
          <h2 className="font-heading text-lg font-medium mt-0.5">Solde à date</h2>
        </div>
        {balance && (
          <div className="text-right">
            <div className={`text-2xl font-mono font-semibold ${
              isDebtor ? "text-[#B24A32]" : isCreditor ? "text-[#3E5C34]" : "text-[#575A57]"
            }`} data-testid="reseller-balance-amount">
              {balance.balance >= 0 ? "+" : "−"} {fmtNum(Math.abs(balance.balance))} CHF
            </div>
            <div className="text-[10px] uppercase tracking-wide text-[#575A57]">
              {isDebtor ? (
                <><TrendingUp className="w-3 h-3 inline mr-0.5" /> Doit à Maison Thaï</>
              ) : isCreditor ? (
                <><TrendingDown className="w-3 h-3 inline mr-0.5" /> Avoir en faveur du client</>
              ) : (
                "Compte à zéro"
              )}
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="text-center py-4 text-[#575A57]"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" />Calcul…</div>
      )}

      {balance && !loading && (
        <>
          <div className="grid grid-cols-3 gap-3 text-sm mb-4" data-testid="reseller-balance-breakdown">
            <div className="border border-[#E5E5E0] rounded p-3 text-center">
              <div className="text-[10px] uppercase text-[#575A57] tracking-wide">Facturé</div>
              <div className="font-mono font-medium text-[#1F4E3D]">{fmtNum(balance.total_invoiced)}</div>
              <div className="text-[10px] text-[#575A57]">{balance.counts.invoices} facture(s)</div>
            </div>
            <div className="border border-[#E5E5E0] rounded p-3 text-center">
              <div className="text-[10px] uppercase text-[#575A57] tracking-wide">Payé</div>
              <div className="font-mono font-medium text-[#3E5C34]">−{fmtNum(balance.total_paid)}</div>
            </div>
            <div className="border border-[#E5E5E0] rounded p-3 text-center">
              <div className="text-[10px] uppercase text-[#575A57] tracking-wide">Avoirs</div>
              <div className="font-mono font-medium text-[#8B3A1F]">−{fmtNum(balance.total_credited)}</div>
              <div className="text-[10px] text-[#575A57]">{balance.counts.credit_notes} avoir(s)</div>
            </div>
          </div>

          <div className="flex items-end gap-2 flex-wrap border-t border-[#E5E5E0] pt-3">
            <div className="flex-1">
              <label className="text-[10px] uppercase text-[#575A57] tracking-wide block mb-0.5">Du</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="text-xs border border-[#E5E5E0] rounded px-2 py-1.5 w-full"
                data-testid="reseller-balance-from"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] uppercase text-[#575A57] tracking-wide block mb-0.5">Au</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="text-xs border border-[#E5E5E0] rounded px-2 py-1.5 w-full"
                data-testid="reseller-balance-to"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadStatement}
              disabled={loading}
              data-testid="reseller-balance-statement"
            >
              <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Relevé
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadPdf}
              disabled={loading}
              className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#EAF4EE]"
              data-testid="reseller-balance-pdf"
              title="Télécharge le relevé PDF corporate imprimable"
            >
              <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
          </div>
          <div className="flex flex-wrap justify-end mt-2 min-w-0">
            <Button
              size="sm"
              variant="outline"
              onClick={downloadOpenItems}
              disabled={loading}
              className="border-[#B24A32]/40 text-[#B24A32] hover:bg-[#B24A32]/5 whitespace-normal h-auto py-1.5 max-w-full"
              data-testid="reseller-open-items-pdf"
              title="Factures non payées, moins les avoirs encore disponibles, état à aujourd'hui"
            >
              <FileDown className="w-3.5 h-3.5 mr-1" /> Relevé des factures ouvertes (PDF)
            </Button>
          </div>

          {showStatement && statement && (
            <div className="mt-4 border border-[#E5E5E0] rounded overflow-hidden" data-testid="reseller-balance-entries">
              <div className="bg-[#FAFAF7] px-3 py-2 text-xs flex items-center justify-between">
                <span>
                  Solde d'ouverture au {statement.from_date} : <strong className="font-mono">{fmtNum(statement.opening_balance)} CHF</strong>
                </span>
                <span>
                  Solde de clôture au {statement.to_date} : <strong className="font-mono">{fmtNum(statement.closing_balance)} CHF</strong>
                </span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#FAFAF7] border-t border-[#E5E5E0]">
                    <th className="text-left px-3 py-1.5">Date</th>
                    <th className="text-left px-3 py-1.5">Réf.</th>
                    <th className="text-left px-3 py-1.5">Description</th>
                    <th className="text-right px-3 py-1.5">Débit</th>
                    <th className="text-right px-3 py-1.5">Crédit</th>
                    <th className="text-right px-3 py-1.5">Solde</th>
                  </tr>
                </thead>
                <tbody>
                  {(statement.entries || []).length === 0 && (
                    <tr><td colSpan={6} className="text-center py-3 text-[#575A57]">Aucun mouvement sur la période</td></tr>
                  )}
                  {(statement.entries || []).map((e, i) => (
                    <tr key={i} className="border-t border-[#E5E5E0]">
                      <td className="px-3 py-1">{e.date}</td>
                      <td className="px-3 py-1 font-mono">{e.reference}</td>
                      <td className="px-3 py-1">{e.description}</td>
                      <td className="px-3 py-1 text-right font-mono">{e.debit > 0 ? fmtNum(e.debit) : ""}</td>
                      <td className="px-3 py-1 text-right font-mono text-[#3E5C34]">{e.credit > 0 ? fmtNum(e.credit) : ""}</td>
                      <td className="px-3 py-1 text-right font-mono">{fmtNum(e.running_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
